import React, { useEffect, useMemo, useState } from 'react';
import { Avatar, Box, Button, Stack, Typography } from '@mui/material';
import styles from './UserInfo.module.scss';
import { useRecoilValue } from 'recoil';
import { currentUserState } from '../../../../atoms/user/user.atom';
import { getAssetLocation } from '../../../../utils/asset/asset.util';
import { AssetTypes } from '../../../../domain/Asset';
import { sidebarExtendedState } from '../../../../atoms/layout/layout.atom';
import { useWindowWidth } from '@react-hook/window-size';
import cx from 'classnames';
import _ from 'lodash';
import { colors } from '../../../../styles/colors';
import { useMutation } from '@tanstack/react-query';
import { SetValues } from '../../../../hooks/useForm/useForm';
import type { DefaultError } from '@tanstack/query-core';
import axios from '../../../../libraries/axios/axios';
import { sidebarCheckinConfig } from '../../../../../config/sidebar/sidebar.config';
import { roleAccessState } from '../../../../atoms/auth/auth.atom';
import { hasRoleAccess } from '../../../../utils/access/access';
import { HttpMethod } from '../../../../domain/Role';
import { translate } from '../../../../utils/translation/translation';
import { translationsState } from '../../../../atoms/translation/translation.atom';
import { toast } from 'sonner';
import { HttpError } from '../../../../types/Api';

export const UserInfo = () => {
  const translations = useRecoilValue(translationsState);
  const sidebarExtendedValue = useRecoilValue(sidebarExtendedState);
  const currentUser = useRecoilValue(currentUserState);
  const roleAccessValue = useRecoilValue(roleAccessState);
  const screenWidth = useWindowWidth();

  const [isCurrentCheckin, setIsCurrentCheckIn] = useState<boolean>(false);

  const { mutate: getStatus } = useMutation<{ data: SetValues }, DefaultError>({
    mutationFn: () => {
      return axios.get(sidebarCheckinConfig.statusApiUrl);
    },
    onSettled: (response) => {
      setIsCurrentCheckIn(!!response?.data ?? false);
    },
  });

  const { mutate: checkIn } = useMutation<{ data: SetValues }, HttpError>({
    mutationFn: () => {
      return axios.post(sidebarCheckinConfig.startApiUrl);
    },
    onSuccess: () => getStatus(),
    onError: (response) => {
      toast.error(
        translate(translations, response?.response?.data?.message ?? ''),
      );
    },
  });

  const { mutate: checkOut } = useMutation<{ data: SetValues }, DefaultError>({
    mutationFn: () => {
      return axios.post(sidebarCheckinConfig.endApiUrl);
    },
    onSuccess: () => getStatus(),
  });

  const isEnabled = useMemo(() => {
    if (!sidebarCheckinConfig.enabled) {
      return false;
    }

    return hasRoleAccess(
      roleAccessValue,
      HttpMethod.GET,
      sidebarCheckinConfig.statusApiUrl,
    );
  }, [roleAccessValue]);

  const avatarOutline = useMemo(() => {
    if (!isEnabled) {
      return 'transparent';
    }

    if (isCurrentCheckin) {
      return colors.alertSuccessBackgroundColor;
    }

    return colors.alertErrorBackgroundColor;
  }, [isCurrentCheckin, isEnabled]);

  const isCollapsed = useMemo(() => {
    if (screenWidth < 1200) {
      return false;
    }

    return !sidebarExtendedValue;
  }, [sidebarExtendedValue, screenWidth]);

  useEffect(() => {
    if (isEnabled) {
      getStatus();
    }
  }, [isEnabled]);

  return (
    <Stack
      className={cx(styles.userInfoContainer, {
        [styles.collapsed]: isCollapsed,
      })}
      style={{ backgroundColor: avatarOutline }}
    >
      <Stack className={styles.userInfoContainerInner}>
        <Box className={styles.avatarContainer}>
          <Avatar
            className={styles.avatar}
            src={
              currentUser?.profile.image
                ? getAssetLocation(
                    currentUser?.profile.image,
                    AssetTypes.IMAGE_THUMB_SMALL,
                  )
                : undefined
            }
          />
        </Box>
        <Stack className={styles.nameContainer}>
          <Typography
            className={styles.name}
            variant="subtitle2"
            style={{ color: colors.textColor }}
          >
            {_.capitalize(currentUser?.profile.firstName)}{' '}
            {_.capitalize(currentUser?.profile.lastName)}
          </Typography>
          <Typography
            className={styles.email}
            variant="body2"
            style={{ color: colors.sidebarGroupColor }}
          >
            {currentUser?.identity}
          </Typography>
          {isEnabled && (
            <Box className={styles.checkInContainer}>
              <Button
                variant={'contained'}
                onClick={() => (isCurrentCheckin ? checkOut() : checkIn())}
              >
                {translate(
                  translations,
                  isCurrentCheckin
                    ? sidebarCheckinConfig.endLabel
                    : sidebarCheckinConfig.startLabel,
                )}
              </Button>
            </Box>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default UserInfo;
